import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { AuthToken, Customer } from '@typesApp/Customer'
import { CustomerContext } from '@typesApp/Customer'
import { useCustomer } from '@hooks/useCustomer'
import { useEnvironment } from '@hooks/useEnvironment'
import { useLazyGetClaimRecordNoCacheQuery } from '@redux/queries/claimRecords'
import { useLinkCustomerMutation } from '@redux/queries/guestCustomer'
import { insuranceListenersSelector } from '@redux/slices/insuranceMemberSlice'
import { formatCommerceCode } from '@utils/formatCommerceCode'
import { getInsuranceToggleState } from '@utils/insuranceToggleUtil'
import { invokeInsuranceActionListeners } from '@utils/invokeInsuranceListeners'
import { isCustomerNumberValid } from '@utils/isCustomerNumberValid'
import { clearLocalAppEventState, getLocalAppEventState, setLocalEventHandled } from '@utils/localAppEventState'
import {
  clearLocalCustomerContextState,
  getLocalCustomerContextState,
  isPersistLocalCustomerContext,
  setLocalCustomerContextState,
} from '@utils/localCustomerContext'
import { logger } from '@utils/logger'

type UseInitTokenActionReturn = {
  beforeInitToken: (customer: Customer, customerContext: CustomerContext) => Promise<void>
  afterInitToken: (customer: Customer, customerContext: CustomerContext, authToken: AuthToken) => Promise<void>
}

export const useInitTokenAction = (): UseInitTokenActionReturn => {
  const { getCustomer } = useCustomer()
  const { anonymousUserId, ecommerceCode, ecommerceTransactionHost } = useEnvironment()
  const [triggerLinkCustomer] = useLinkCustomerMutation()
  const safeInvokeInsuranceListeners = useInvokeInsuranceListenersOnLogin()

  /**
   * Link guest customer to registered customer on login.
   *
   * NOTE: the linkCustomer API is called with the guest customer token
   */
  const linkCustomer = useCallback(
    async (guestCustomerNumber: string, guestCustomerAccessToken: string) => {
      // always fetch latest customer from commerce
      const customer = await getCustomer()
      const {
        id: customerNumber,
        firstName: customerFirstName,
        lastName: customerLastName,
        email: customerEmail,
      } = customer ?? {}

      if (customerNumber) {
        const ecommerce = formatCommerceCode({
          ecommerceCode,
          ecommerceTransactionHost,
        })

        await triggerLinkCustomer({
          guestCustomerNumber,
          guestCustomerAccessToken,
          ecommerce,
          customerNumber,
          customerFirstName,
          customerLastName,
          customerEmail,
        }).unwrap()
      }
    },
    [triggerLinkCustomer, getCustomer, ecommerceCode, ecommerceTransactionHost]
  )

  const linkGuestCustomerOnLogin = useCallback(async () => {
    // need to check from local customer context as commerce may have
    // reloaded the page on customer login
    const localContext = getLocalCustomerContextState()
    const { guestCustomerNumber, accessToken } = localContext ?? {}

    // check if we have a pending login event
    const { type, isHandled } = getLocalAppEventState()
    const isLoginEvent = type === 'onLogin'
    const isLoginEventHandled = isLoginEvent && isHandled

    const shouldLinkCustomer =
      isLoginEvent &&
      !isLoginEventHandled &&
      guestCustomerNumber &&
      accessToken &&
      isCustomerNumberValid(guestCustomerNumber, anonymousUserId)

    // allow error to propagate and cause initToken to fail
    if (shouldLinkCustomer) {
      await linkCustomer(guestCustomerNumber, accessToken)
    }

    // mark login event handled only if linkCustomer was successful or not needed
    setLocalEventHandled('onLogin')
  }, [linkCustomer, anonymousUserId])

  /**
   * Note: errors from this call will propagate and cause initToken to fail.
   * If linkCustomer fails, we want the initToken to fail.
   */
  const beforeInitToken = useCallback(async () => {
    await linkGuestCustomerOnLogin()
  }, [linkGuestCustomerOnLogin])

  const handleLoginActions = useCallback(
    async (authToken: AuthToken) => {
      const { type, isHandled } = getLocalAppEventState()
      const isLoginEvent = type === 'onLogin'
      const isLoginEventHandled = isLoginEvent && isHandled

      if (isLoginEvent) {
        await safeInvokeInsuranceListeners(authToken.claimRecordId)
      }

      if (isLoginEventHandled) {
        clearLocalAppEventState()
        clearLocalCustomerContextState()
      }
    },
    [safeInvokeInsuranceListeners]
  )

  /**
   * Auth token has been succesfully fetched - actions within this call should not throw errors
   */
  const afterInitToken = useCallback(
    async (customer: Customer, customerContext: CustomerContext, authToken: AuthToken) => {
      persistLocalCustomerContext(customerContext, authToken)

      await handleLoginActions(authToken)
    },
    [handleLoginActions]
  )

  return {
    beforeInitToken,
    afterInitToken,
  }
}

/**
 * Persist local state for guest customer so that on next token fetch
 * we have access to the guest customer context in case commerce reloads
 * page on login
 */
const persistLocalCustomerContext = (customerContext: CustomerContext, authToken: AuthToken) => {
  if (isPersistLocalCustomerContext(customerContext, authToken)) {
    setLocalCustomerContextState(authToken)
  }
}

type UseInvokeInsuranceListenersOnLoginReturn = (claimRecordId: string | undefined) => Promise<void>

/**
 * US flow - on login, invoke the apply insurance action if customer has insurance applied
 */
const useInvokeInsuranceListenersOnLogin = (): UseInvokeInsuranceListenersOnLoginReturn => {
  const listenerList = useSelector(insuranceListenersSelector)
  const [fetchClaimRecord] = useLazyGetClaimRecordNoCacheQuery()

  return useCallback(
    async (claimRecordId: string | undefined) => {
      if (!claimRecordId) return

      try {
        const claimRecord = await fetchClaimRecord(claimRecordId).unwrap()
        const { toggleOn } = getInsuranceToggleState({ claimRecord })
        invokeInsuranceActionListeners(listenerList, toggleOn ? 'apply' : 'remove')
      } catch (error) {
        logger.warn('Error invoking insurance action listeners', error)
      }
    },
    [fetchClaimRecord, listenerList]
  )
}
